import { Helmet, HelmetData } from "react-helmet-async";
import { useRef } from "react";
import {
  AI_BG_NEW,
  Business_Icon,
  Education_Icon,
  Every_African_Lang_Img,
  Government_Icon,
  Healthcare_Icon,
  Local_Language_Hero_Image,
  Local_Matter_Img,
  Map_Overylay_Img,
  Transforming_Low_Resource_Img,
  Use_Case_Bg,
} from "../../images";
import BookConsultation from "../book-a-consultation";
import { NewFooter } from "../newFooter";
import useIntersectionObserver from "../../utils/useIntersectionObserver";

export default function LocalLanguage() {
  const targetRef = useRef(null);

  useIntersectionObserver({ targetRef });

  const helmetData = new HelmetData({});

  const help = [
    "Enable over 1 billion Africans to access AI in their native languages",
    "Preserve linguistic and cultural heritage in the digital age",
    "Create economic opportunities in local markets",
    "Improve education outcomes through mother-tongue learning",
    "Foster innovation in African tech ecosystems",
  ];

  const why = [
    {
      icon: Education_Icon,
      title: "Education",
      text: "AI tutors and learning platforms in native languages",
    },
    {
      icon: Healthcare_Icon,
      title: "Healthcare",
      text: "Medical chatbots and diagnostic support tools",
    },
    {
      icon: Government_Icon,
      title: "Government",
      text: "A platform to build, fine-tune, and deploy AI with minimal engineering lift.",
    },
    {
      icon: Business_Icon,
      title: "Business",
      text: "Customer service, content creation, and market research",
    },
  ];

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>Local Language LLMs for Africa | African Language AI Models | Analytics Intelligence Africa</title>

        <link
          rel="canonical"
          href="https://africa.analyticsintelligence.com/local-language-llms"
        />

        <meta
          name="description"
          content="Analytics Intelligence Africa is building local language large language models (LLMs) for Africa — empowering Yoruba, Swahili, Hausa, Amharic, and more. Transforming low-resource African languages into digital assets for inclusive AI adoption across the continent."
        />

        <meta
          name="keywords"
          content="Analytics Intelligence Africa, local language LLMs Africa, African language AI, African language models, low-resource language AI Africa, multilingual NLP Africa, Yoruba AI, Swahili AI, Hausa AI, Amharic AI, African languages technology, inclusive AI Africa, language models Africa, African NLP, AI for Africa, data annotation Africa, African AI models"
        />

        <meta property="og:title" content="Local Language LLMs for Africa | African Language AI Models | Analytics Intelligence Africa" />
        <meta property="og:description" content="Building large language models that understand and speak Africa — empowering over 1 billion Africans to access AI in their native languages." />
        <meta property="og:url" content="https://africa.analyticsintelligence.com/local-language-llms" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Local Language LLMs for Africa | Analytics Intelligence Africa" />
        <meta name="twitter:description" content="Building large language models that understand and speak Africa — empowering over 1 billion Africans to access AI in their native languages." />
        <meta name="twitter:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Local Language LLMs for Africa",
            "url": "https://africa.analyticsintelligence.com/local-language-llms",
            "description": "Building local language large language models that understand African languages — Yoruba, Swahili, Hausa, Amharic — empowering over 1 billion Africans to access AI in their native tongues.",
            "provider": {
              "@type": "Organization",
              "name": "Analytics Intelligence Africa",
              "url": "https://africa.analyticsintelligence.com"
            },
            "areaServed": ["Nigeria", "Ghana", "Kenya", "South Africa", "Ethiopia", "Rwanda", "Uganda", "Africa"],
            "knowsAbout": ["African Language AI", "Local Language LLMs", "Multilingual NLP", "Low-Resource Language Models", "African NLP Research"]
          }
        `}</script>
      </Helmet>

      <main>
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundSize: "cover",
          }}
          className="min-h-[100vh] flex items-center bg-left-bottom md:bg-center"
          ref={targetRef}
        >
          <div className="container mx-auto w-full h-full flex flex-col md:flex-row justify-between items-center gap-16  pt-[126px] pb-11 md:py-[96px] py-[96px]">
            <div className="flex basis-2/4 flex-col flex-wrap gap-[16px] items-center md:items-start">
              <div className="space-y-8 md:space-y-11 flex flex-col items-center md:items-start">
                <span className="text-sm md:text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                  Local Language LLMs
                </span>
                <div className="space-y-4">
                  <h2 className="text-4xl md:text-[40px] xl:text-[48px] text-center md:text-left font-extrabold leading-[1.3] md:leading-[54px] text-white">
                    Bringing African <br className="hidden xl:block" />{" "}
                    Languages to the AI Era
                  </h2>
                  <p className="text-sm md:text-lg text-center md:text-left font-medium text-[#E3E3E3] leading-5">
                    We’re building large language models that understand and
                    speak Africa, making local languages part of the global AI
                    conversation.
                  </p>
                </div>
              </div>
            </div>
            <div className="basis-2/4 flex justify-end">
              <img
                className=" w-full"
                src={Local_Language_Hero_Image}
                alt="illustration"
              />
            </div>
          </div>
        </section>

        <section
          className="relative w-full bg-white py-11 md:py-[80px] xl:py-[100px] bg-top bg-contain bg-no-repeat"
          style={{
            backgroundImage: `url("${Map_Overylay_Img}")`,
          }}
        >
          <div className="mx-auto container">
            <div className="h-[300px] w-[300px] xl:h-[450px] xl:w-[450px] bg-[#7AA8FF] blur-[180px] absolute top-0 -right-[70%] md:-right-[45%] lg:-right-[15%]"></div>
            <div className="mb-[32px] md:mb-[100px] flex flex-col items-center gap-8 md:grid md:gap-12 lg:gap[65px] xl:gap-[88px] md:grid-cols-2">
              <div className="space-y-4">
                <h2 className="text-2xl xl:text-[32px] font-bold !leading-[120%]">
                  Transforming Low-Resource Languages into Digital Assets
                </h2>
                <p className="text-base md:text-lg xl:text-xl font-medium text-[#333333]">
                  African languages have long been underrepresented in
                  technology. We’re building local language LLMs that turn them
                  into digital assets, empowering over a billion Africans to
                  connect and create in their native tongues.
                </p>
              </div>

              <img
                src={Transforming_Low_Resource_Img}
                alt="Woman using laptop"
                className="h-auto w-full max-w-[640px] object-cover"
              />
            </div>

            <div className="mb-[32px] md:mb-[100px] flex flex-col-reverse items-center gap-8 md:grid md:gap-12 lg:gap[65px] xl:gap-[88px] md:grid-cols-2">
              <img
                src={Every_African_Lang_Img}
                alt="Woman using laptop"
                className="h-auto w-full max-w-[640px] object-cover"
              />
              <div className="space-y-4">
                <h2 className="text-2xl xl:text-[32px] font-bold !leading-[120%]">
                  Giving Every African Language a Digital Voice
                </h2>
                <p className="text-base md:text-lg xl:text-xl font-medium text-[#333333]">
                  We’re creating advanced language models designed to understand
                  and represent Africa’s linguistic diversity, from Swahili and
                  Yoruba to Amharic and Hausa.
                </p>
                <p className="text-base md:text-lg xl:text-xl">
                  <span className="font-bold ">Multilingual Coverage</span>:
                  Building LLMs across major African language families across
                  regions.
                </p>
                <p className="text-base md:text-lg xl:text-xl">
                  <span className="font-bold ">Advanced NLP Research</span>:
                  Tackling low-resource challenges with new NLP breakthroughs. 
                </p>
                <p className="text-base md:text-lg xl:text-xl">
                  <span className="font-bold ">Translation Excellence</span>:
                  High-quality translation between African languages and global
                  languages, breaking down communication barriers.
                </p>
              </div>
            </div>

            {/* What We Do Section */}
            <div className="grid items-center gap-12 lg:gap[65px] xl:gap-[88px] md:grid-cols-2">
              {/* Left side - text content */}
              <div className="space-y-3 md:space-y-6">
                <h3 className="text-2xl xl:text-[32px] font-bold">
                  Why Local Language LLMs Matter
                </h3>

                <div className="space-y-3">
                  <p className="font-bold text-base md:text-lg xl:text-xl text-gray-900">
                    We help you:
                  </p>
                  <ul className="space-y-2">
                    {help.map((item, index) => (
                      <li
                        key={index}
                        className="text-sm md:text-base flex items-center gap-2 text-gray-700"
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect width="24" height="24" rx="12" fill="#EDF9FF" />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M17.096 7.39016L9.93602 14.3002L8.03602 12.2702C7.68602 11.9402 7.13602 11.9202 6.73602 12.2002C6.34602 12.4902 6.23602 13.0002 6.47602 13.4102L8.72602 17.0702C8.94602 17.4102 9.32601 17.6202 9.75601 17.6202C10.166 17.6202 10.556 17.4102 10.776 17.0702C11.136 16.6002 18.006 8.41016 18.006 8.41016C18.906 7.49016 17.816 6.68016 17.096 7.38016V7.39016Z"
                            fill="#0073FF"
                          />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <img
                src={Local_Matter_Img}
                alt="Man working with data analytics"
                className="h-auto w-full max-w-[640px] object-cover"
              />
            </div>
          </div>
        </section>

        <section
          className="bg-[#EDF1FF] py-[48px] bg-left-bottom md:bg-center bg-cover md:bg-cover"
          style={{
            backgroundImage: `url("${Use_Case_Bg}")`,
          }}
        >
          <div className="container space-y-12">
            <div className="mb-20 text-center">
              <h1 className="mb-2 text-2xl md:text-3xl font-bold tracking-tight xl:text-[36px] text-[#FFF]">
                Use Cases
              </h1>
            </div>

            <div className="grid grid-cols-12 gap-y-8 sm:gap-8">
              {why.map((item, idx) => (
                <div
                  key={idx}
                  className="col-span-12 sm:col-span-6 xl:col-span-3 bg-white flex flex-col items-center rounded-xl px-3 py-6 gap-4"
                >
                  <img
                    src={item.icon}
                    alt={`${item.title} illustration`}
                    className="w-16 h-16"
                  />
                  <div className="space-y-2 text-center">
                    <h5 className="text-lg md:text-xl font-bold text-[#020128]">
                      {item.title}
                    </h5>
                    <p className="text-sm md:text-base text-[#020128]">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <NewFooter>
          <BookConsultation
            title="Let’s help you annotate and label data that AI can use"
            text="We fine-tune models, streamline data, and scale your AI stack
                 effortlessly."
            btnText="Book a Consultation"
          />
        </NewFooter>
      </main>
    </>
  );
}
